import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { shortTime, money } from "../format";

interface Props {
  data: { t: string; equity: number }[];
  label: string;
  height?: number;
}

/**
 * Equity curve. Accessible: the SVG is decorative (aria-hidden) and a concise
 * text summary + a screen-reader-only data table convey the same information.
 */
export default function EquityChart({ data, label, height = 220 }: Props) {
  if (!data || data.length === 0)
    return <p className="muted">No equity data yet for {label}.</p>;

  const first = data[0].equity;
  const last = data[data.length - 1].equity;
  const change = first ? (last - first) / first : 0;
  const summary = `${label}: equity from ${money(first)} to ${money(last)} (${(change * 100).toFixed(1)}%) over ${data.length} bars.`;

  return (
    <figure className="chart" role="group" aria-label={`Equity curve for ${label}`}>
      <p className="chart-summary">{summary}</p>
      <div aria-hidden="true">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
            <XAxis dataKey="t" tickFormatter={shortTime} minTickGap={48}
              tick={{ fontSize: 11, fill: "var(--muted)" }} />
            <YAxis domain={["auto", "auto"]} tickFormatter={(v) => money(v)}
              width={64} tick={{ fontSize: 11, fill: "var(--muted)" }} />
            <Tooltip
              formatter={(v: number) => money(v)}
              labelFormatter={shortTime}
              contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", color: "var(--fg)" }}
            />
            <Line type="monotone" dataKey="equity" stroke="var(--accent)"
              strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <table className="visually-hidden">
        <caption>Equity over time for {label}</caption>
        <thead><tr><th scope="col">Time</th><th scope="col">Equity</th></tr></thead>
        <tbody>
          {data.filter((_, i) => i % Math.ceil(data.length / 30) === 0).map((d) => (
            <tr key={d.t}><td>{d.t}</td><td>{d.equity.toFixed(2)}</td></tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
